import type { InvoiceAdditionalFilters } from '../../../types/invoice';
import type { FlagTypeKey, InvoiceTypeKey } from '../../../types/reporting';
import type { InvoicesViewStateCache } from '../../../shared/invoicesListViewState';
import {
    getPeriodBucketDisplayStartDate,
    getPeriodBucketEndDate,
} from '../shared/dateUtils';

const INVOICE_TYPES: InvoiceTypeKey[] = ['received', 'returned', 'return_slip', 'sublet'];

type FeedFlagReason = NonNullable<InvoiceAdditionalFilters['flaggedReasons']>[number];

/** Chart slices that have an upload-snapshot flag on the feed. Error is a reporting bucket with no feed flag. */
const FEED_FLAG_BY_CHART: Record<FlagTypeKey, FeedFlagReason | null> = {
    ro: 'ro',
    match: 'nex',
    duplicate: 'dupe',
    math: 'math',
    vendor: 'vendor',
    specialVendor: 'specialVendor',
    locationMismatch: 'locationMismatch',
    stale: 'stale',
    price: 'price',
    quantity: 'qty',
    amount: 'total',
    error: null,
};

export interface InvoicingChartSegmentClick {
    /**
     * `flag:amount`, `type:received`, `loc:<id>`, `vend:<id>`, `vendtype:<invoiceType>`,
     * `total`, `billable:postedOpen|postedArchived|flaggedOpen|flaggedArchived`, or
     * `efficiency:theirFault|neededFlags|match`.
     * `vendtype` uses `seriesLabel` as the vendor name.
     */
    seriesKey: string;
    seriesLabel?: string;
    /** Time-series bar bucket start (YYYY-MM-DD). Omit for doughnuts and category bars. */
    bucketStart?: string;
}

export interface InvoicingChartDrillInput {
    startDate: string;
    endDate: string;
    granularity?: string;
    /** Single location when reporting is already scoped to one shop. */
    locationId?: string;
    locationIds?: string[];
    vendorNames?: string[];
    invoiceTypes?: InvoiceTypeKey[];
    flagTypes?: FlagTypeKey[];
    flagMatch?: 'any' | 'exact';
    segment: InvoicingChartSegmentClick;
}

function inclusiveLocalRange(start: string, end: string): { start: Date; end: Date } {
    const [startYear, startMonth, startDay] = start.split('-').map(Number);
    const [endYear, endMonth, endDay] = end.split('-').map(Number);
    return {
        start: new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0),
        end: new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999),
    };
}

function isInvoiceType(value: string): value is InvoiceTypeKey {
    return INVOICE_TYPES.includes(value as InvoiceTypeKey);
}

function isFlagType(value: string): value is FlagTypeKey {
    return Object.prototype.hasOwnProperty.call(FEED_FLAG_BY_CHART, value);
}

type EfficiencyFlag = NonNullable<InvoiceAdditionalFilters['efficiencyFlags']>[number];

interface CompositionFeedTarget {
    type: 'flagged' | 'submitted';
    showArchived: boolean;
    efficiencyFlag: EfficiencyFlag | null;
}

function compositionFeedTarget(seriesKey: string): CompositionFeedTarget | 'other' | null {
    if (seriesKey.startsWith('billable:')) {
        switch (seriesKey.slice('billable:'.length)) {
            case 'postedOpen':
                return { type: 'submitted', showArchived: false, efficiencyFlag: null };
            case 'postedArchived':
                return { type: 'submitted', showArchived: true, efficiencyFlag: null };
            case 'flaggedOpen':
                return { type: 'flagged', showArchived: false, efficiencyFlag: null };
            case 'flaggedArchived':
                return { type: 'flagged', showArchived: true, efficiencyFlag: null };
            default:
                return null;
        }
    }
    if (seriesKey.startsWith('efficiency:')) {
        switch (seriesKey.slice('efficiency:'.length)) {
            case 'theirFault':
                return { type: 'submitted', showArchived: false, efficiencyFlag: 'theirFault' };
            case 'neededFlags':
                return { type: 'submitted', showArchived: false, efficiencyFlag: 'neededFlags' };
            case 'match':
                return { type: 'submitted', showArchived: false, efficiencyFlag: 'match' };
            default:
                return null;
        }
    }
    return 'other';
}

function feedFlags(flagTypes: readonly FlagTypeKey[]): { reasons: FeedFlagReason[]; droppedUnmapped: boolean } {
    const reasons: FeedFlagReason[] = [];
    let droppedUnmapped = false;
    for (const flag of flagTypes) {
        const mapped = FEED_FLAG_BY_CHART[flag];
        if (!mapped) {
            droppedUnmapped = true;
            continue;
        }
        if (!reasons.includes(mapped)) reasons.push(mapped);
    }
    return { reasons, droppedUnmapped };
}

/**
 * Feed view for a reporting chart click.
 * Returns null when the slice cannot be expressed as feed filters (the Error slice,
 * or an unknown composition slice).
 */
export function buildInvoicingChartDrill(input: InvoicingChartDrillInput): InvoicesViewStateCache | null {
    if (!input.startDate || !input.endDate) return null;

    let locationIds: Array<string> | 'all' = input.locationIds?.length
        ? [...input.locationIds]
        : input.locationId
            ? [input.locationId]
            : 'all';
    let vendorNames = input.vendorNames ? [...input.vendorNames] : [];
    let invoiceTypes = input.invoiceTypes ? [...input.invoiceTypes] : [];
    let flagTypes = input.flagTypes ? [...input.flagTypes] : [];
    let flagMatch: 'any' | 'exact' = input.flagMatch === 'exact' ? 'exact' : 'any';
    let feedType: InvoicesViewStateCache['type'] = 'all';
    let showArchived = false;
    let efficiencyFlag: EfficiencyFlag | null = null;

    const { seriesKey } = input.segment;
    const composition = compositionFeedTarget(seriesKey);
    if (composition === null) return null;
    if (composition !== 'other') {
        feedType = composition.type;
        showArchived = composition.showArchived;
        efficiencyFlag = composition.efficiencyFlag;
    } else if (seriesKey.startsWith('flag:')) {
        const chartFlag = seriesKey.slice(5);
        if (!isFlagType(chartFlag) || FEED_FLAG_BY_CHART[chartFlag] == null) return null;
        const keepsExactSet = flagMatch === 'exact' && flagTypes.includes(chartFlag);
        if (!keepsExactSet) {
            flagTypes = [chartFlag];
            flagMatch = 'any';
        }
    } else if (seriesKey.startsWith('type:')) {
        const invoiceType = seriesKey.slice(5);
        if (!isInvoiceType(invoiceType)) return null;
        invoiceTypes = [invoiceType];
    } else if (seriesKey.startsWith('loc:')) {
        const id = seriesKey.slice(4);
        if (!id) return null;
        locationIds = [id];
    } else if (seriesKey.startsWith('vendtype:')) {
        const invoiceType = seriesKey.slice('vendtype:'.length);
        const vendorName = input.segment.seriesLabel?.trim() ?? '';
        if (!isInvoiceType(invoiceType) || !vendorName) return null;
        invoiceTypes = [invoiceType];
        vendorNames = [vendorName];
    } else if (seriesKey.startsWith('vend:')) {
        vendorNames = [input.segment.seriesLabel ?? ''];
    }

    let rangeStart = input.startDate;
    let rangeEnd = input.endDate;
    if (input.segment.bucketStart) {
        rangeStart = getPeriodBucketDisplayStartDate(input.segment.bucketStart, input.startDate);
        rangeEnd = getPeriodBucketEndDate(input.segment.bucketStart, input.granularity ?? 'week', input.endDate);
        if (rangeEnd < rangeStart) rangeEnd = rangeStart;
    }

    const additionalFilters: InvoiceAdditionalFilters = {
        uploadDateSort: 'range',
        uploadDateRange: inclusiveLocalRange(rangeStart, rangeEnd),
    };
    if (invoiceTypes.length > 0) additionalFilters.invoiceTypes = invoiceTypes;
    if (vendorNames.length > 0) additionalFilters.vendorNames = vendorNames;
    const mappedFlags = feedFlags(flagTypes);
    if (mappedFlags.reasons.length > 0) {
        additionalFilters.flaggedReasons = mappedFlags.reasons;
        if (flagMatch === 'exact' && !mappedFlags.droppedUnmapped) {
            additionalFilters.flaggedReasonsMatch = 'exact';
        }
    }
    if (efficiencyFlag) additionalFilters.efficiencyFlags = [efficiencyFlag];

    return {
        type: feedType,
        showArchived,
        showNegotiation: false,
        selectedNegotiationId: null,
        currentPage: 1,
        searchQuery: '',
        debouncedSearch: '',
        additionalFilters,
        locationIds,
    };
}
